import { keyframes } from "@emotion/react";
import {
  Box,
  Button,
  Icon,
  SxProps,
  Typography,
  useTheme
} from "@outschool/backpack";
import { faWandMagicSparkles } from "@outschool/icons";
import { TFunction, useTranslation } from "@outschool/localization";
import { dayjs, formatDate, timeZoneName } from "@outschool/time";
import { useSession } from "@outschool/ui-auth";
import { FieldErrorMessage } from "@outschool/ui-components-shared";
import { Space, TextArea } from "@outschool/ui-legacy-component-library";
import React from "react";

export interface HeaderProps {
  userTimeZone?: string | null;
  t: TFunction;
  lastProgressReportDate?: Date;
}

export default function NoteMakerInput({
  learner,
  teacherNotes,
  createProgressReport,
  setTeacherNotes,
  confimationError,
  setConfirmationError,
  isGeneratingProgressReport,
  sendProgressReportMessage,
  isSendingProgressReport,
  lastProgressReportDate,
  isTablet,
  boxSx,
  textAreaHeight,
  Header = ({ userTimeZone, t, lastProgressReportDate }: HeaderProps) => (
    <Box
      flex
      sx={{
        flexDirection: "row",
        justifyContent: "space-between",
        padding: "1em",
        marginTop: "2em",
        width: "100%"
      }}
    >
      <Typography variant="h5">
        {t("Tell us how {{name}} did", { name: learner?.name ?? t("they") })}
      </Typography>
      {lastProgressReportDate && (
        <Typography
          variant="body1"
          sx={{
            alignSelf: "center"
          }}
        >
          {t("Last note sent: {{lastProgressReportDate}} {{timeZone}}", {
            lastProgressReportDate: formatDate(
              dayjs(lastProgressReportDate),
              userTimeZone ?? undefined,
              true
            ),
            timeZone: timeZoneName(userTimeZone ?? undefined)
          })}
        </Typography>
      )}
    </Box>
  )
}: {
  learner?: { uid: string | undefined; name: string | undefined } | undefined;
  teacherNotes: string | undefined;
  createProgressReport: (teacherNotes: string | undefined) => void;
  setTeacherNotes: React.Dispatch<React.SetStateAction<string>>;
  confimationError: string | undefined;
  setConfirmationError: React.Dispatch<
    React.SetStateAction<string | undefined>
  >;
  isGeneratingProgressReport: boolean;
  sendProgressReportMessage: (
    providedMessage: string | undefined,
    reportLength: string,
    generatedReport: boolean,
    changedGeneratedReport: boolean
  ) => void;
  isSendingProgressReport: boolean | undefined;
  lastProgressReportDate: Date | undefined;
  isTablet: boolean;
  boxSx?: SxProps;
  Header?: React.FunctionComponent<HeaderProps>;
  textAreaHeight?: number;
}) {
  const { t } = useTranslation("client\\components\\NoteMakerInput");
  const theme = useTheme();
  const { userTimeZone } = useSession();
  const loadingText = ["", ".", "..", "..."];
  const [loadingTextIdx, setLoadingTextIdx] = React.useState(0);

  const fadeInAndOut = keyframes`
    0% {
      opacity: 0;
    }
    50% {
      opacity: 1;
    }
    100% {
      opacity: 0;
    }
  `;

  React.useEffect(() => {
    if (isGeneratingProgressReport) {
      const timer = setTimeout(() => {
        setLoadingTextIdx((loadingTextIdx + 1) % loadingText.length);
      }, 600);
      return () => clearTimeout(timer);
    } else {
      return;
    }
  }, [loadingTextIdx, isGeneratingProgressReport, loadingText.length]);

  const animatedButtonText = loadingText[loadingTextIdx];

  return (
    <Box
      flex
      sx={{
        backgroundColor: theme.palette.common.white,
        flexDirection: "column",
        gap: "0.5em",
        boxShadow: "3px 3px 5px 2px rgba(23, 25, 28, 0.1)",
        alignItems: "flex-start",
        borderRadius: "0px 0px 8px 8px",
        width: "100%",
        ...boxSx
      }}
    >
      <Header
        userTimeZone={userTimeZone}
        lastProgressReportDate={lastProgressReportDate}
        t={t}
      />
      <Box
        sx={{
          marginBottom: "0.5em",
          width: "100%",
          minHeight: textAreaHeight ?? "12em"
        }}
      >
        <TextArea
          sx={{ width: "100%", minHeight: textAreaHeight ?? "12em" }}
          value={teacherNotes}
          onChange={async (e: React.FormEvent<HTMLTextAreaElement>) => {
            setConfirmationError(undefined);
            setTeacherNotes((e.target as HTMLTextAreaElement).value);
          }}
          placeholder={t(
            "Example: \n\
- Today we covered fractions \n\
- He showed great focus and enthusiasm \n\
- He struggled with common denominator work \n\
- Homework pages 4-6"
          )}
          showCount={false}
          maxLength={1000}
        />
        {confimationError && <FieldErrorMessage value={confimationError} />}
      </Box>
      <Box
        flex
        sx={(theme: $TSFixMe) => ({
          flexDirection: "row",

          [theme.breakpoints.down("sm")]: {
            flexDirection: "column",
            width: "100%"
          }
        })}
      >
        <Button
          variant="contained"
          onClick={async () => {
            setConfirmationError(undefined);
            await createProgressReport(teacherNotes);
          }}
          disabled={isGeneratingProgressReport || isSendingProgressReport}
          startIcon={
            !isGeneratingProgressReport && (
              <Icon icon={faWandMagicSparkles} sx={{ marginRight: "0.5em" }} />
            )
          }
          sx={(theme: $TSFixMe) => ({
            height: "3em",

            "&:disabled": {
              backgroundColor: theme.palette.primary[400],
              color: theme.palette.common.white
            },

            [theme.breakpoints.down("sm")]: {
              width: "100%"
            }
          })}
        >
          {isGeneratingProgressReport ? (
            <>
              <Typography variant="h6">{t("Making the magic")}</Typography>
              <Space x="0.1em" />
              <Typography
                variant="h6"
                sx={{
                  animation: `${fadeInAndOut} 0.6s`,
                  animationIterationCount: "infinite",
                  width: "0.8em"
                }}
              >
                {animatedButtonText}
              </Typography>
            </>
          ) : (
            t("Show me the magic")
          )}
        </Button>
        <Button
          sx={(theme: $TSFixMe) => ({
            marginLeft: "1.5em",
            marginTop: isTablet ? "1.5em" : "0em",

            [theme.breakpoints.down("sm")]: {
              marginTop: "1.5em",
              width: "100%",
              marginLeft: 0
            }
          })}
          variant="link"
          onClick={async () => {
            setConfirmationError(undefined);
            await sendProgressReportMessage(
              teacherNotes,
              "OneParagraph",
              false,
              false
            );
          }}
          disabled={isSendingProgressReport || isGeneratingProgressReport}
        >
          {t("Send Note to Family")}
        </Button>
      </Box>
    </Box>
  );
}
